// entityServiceFactory.js
// Factory che genera funzioni di servizio per entità paginate con views (annunci, comunicazioni).

import apiClient from './apiClient';

const hasTotal = (obj) => obj && Object.prototype.hasOwnProperty.call(obj, 'total');

// announcements.php / communications.php restituiscono gli allegati come stringa JSON
// ('[{"name":…,"url":…}]'): li convertiamo una volta sola qui, così i componenti
// ricevono sempre un array e non devono fare JSON.parse durante il rendering.
// Senza allegati announcements.php restituisce '[{"name": null, "url": null}]'
// (LEFT JOIN + JSON_OBJECT): le voci senza nome o url vengono scartate qui.
const isRealAttachment = (att) => Boolean(att && att.name && att.url);

const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string' || value.trim() === '') return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const parseAttachments = (value) => toArray(value).filter(isRealAttachment);

const createEntityService = ({ endpoint, idParam, viewAction, viewReturnKey }) => ({

    markAsRead: async (entityId, userId) => {
        const response = await apiClient.post(
            `${endpoint}?action=markAsRead`,
            { [idParam]: entityId, user_id: userId },
            { headers: { 'Content-Type': 'application/json' } }
        );
        return response.data;
    },

    fetch: async (userId, isDirector, hideRead, page = 1, pageSize = 10) => {
        if (!userId) {
            console.error('Errore: userId non definito');
            return { status: 'error', data: [], total: 0, page, pageSize };
        }
        try {
            const response = await apiClient.get(
                `${endpoint}?action=fetch&user_id=${userId}&is_director=${isDirector}&hide_read=${hideRead}&page=${page}&pageSize=${pageSize}`
            );
            const data = response.data;
            if (!data || data.status !== 'success' || !Array.isArray(data.data)) {
                console.error('Errore: risposta non valida o vuota:', data);
                return { status: 'error', data: [], total: 0, page, pageSize };
            }
            return {
                status: 'success',
                data: data.data.map(item => ({ ...item, attachments: parseAttachments(item.attachments) })),
                total: hasTotal(data) ? data.total : data.data.length,
                page: data.page ?? page,
                pageSize: data.pageSize ?? pageSize,
            };
        } catch (error) {
            console.error(`Errore nel recupero da ${endpoint}:`, error);
            return { status: 'error', data: [], total: 0, page, pageSize };
        }
    },

    getViews: async (ids) => {
        if (!Array.isArray(ids) || ids.length === 0) {
            console.error('Nessun ID fornito per recuperare le visualizzazioni.');
            return [];
        }
        try {
            const views = await Promise.all(
                ids.map(async (id) => {
                    const response = await apiClient.get(
                        `/views.php?action=${viewAction}&${idParam}=${id}`
                    );
                    return { [viewReturnKey || idParam]: id, views: response.data };
                })
            );
            return views;
        } catch (error) {
            console.error(`Errore nel recupero views da ${endpoint}:`, error);
            throw error;
        }
    },

    add: async (formData) => {
        try {
            const response = await apiClient.post(`${endpoint}?action=create`, formData);
            return response.data;
        } catch (error) {
            console.error(`Errore in add su ${endpoint}:`, error.response?.data ?? error.message);
            throw error;
        }
    },

    remove: async (entityId) => {
        const response = await apiClient.delete(`${endpoint}?action=delete&${idParam}=${entityId}`);
        return response.data;
    },

    update: async (formData) => {
        try {
            const response = await apiClient.post(`${endpoint}?action=update`, formData);
            return response.data;
        } catch (error) {
            console.error(`Errore in update su ${endpoint}:`, error);
            throw error;
        }
    },
});

export default createEntityService;
